import { useFormikContext } from "formik";
import { AddJournalEntryTable } from "./AddJournalEntryTable";
import { JournalEntryTransaction } from "./AddJournalEntryModal";
import Card from "components/DesignSystem/Card/Card";
import { pluralize } from "utils/pluralize";
import { useMemo } from "react";
import { LedgerTypeKeys } from "constants/ledgerEntryTypes";
import { Info } from "components/icons/Info";
import { Divider } from "components/design/Divider";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import randomBytes from "randombytes";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { getAccrualEntries, getCashEntries } from "utils/journalEntryUtils";
import { ACCOUNTING_METHODS } from "constants/accountMethod";

export const AddEditEntries = ({
  cashAndAccrualMatch,
  isEditFlow,
  maxSerialNumber,
}: {
  cashAndAccrualMatch: boolean;
  isEditFlow?: boolean;
  maxSerialNumber: string;
}) => {
  const { values, setFieldValue } = useFormikContext<{
    transactions: JournalEntryTransaction[];
    ledger_type: LedgerTypeKeys;
    is_cash_same_as_accrual: boolean;
    basis: string;
  }>();

  const accrualEntries = useMemo(
    () => getAccrualEntries(values.transactions),
    [values.transactions]
  );
  const cashEntries = useMemo(
    () => getCashEntries(values.transactions),
    [values.transactions]
  );

  const onChange = (checked: boolean) => {
    setFieldValue("is_cash_same_as_accrual", !values.is_cash_same_as_accrual);

    if (checked) {
      return setFieldValue("transactions", accrualEntries);
    }

    return setFieldValue("transactions", [
      ...accrualEntries,
      ...accrualEntries.map((t) => ({
        ...t,
        cash_category_id: t.accrual_category_id,
        accrual_category_id: "",
        entry_type: ACCOUNTING_METHODS.CASH,
        id: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
        uuid: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
        transaction_id: undefined,
      })),
    ]);
  };

  const showAccrualSection =
    values.basis === ACCOUNTING_METHODS.ACCRUAL ||
    values.basis === ACCOUNTING_METHODS.CASH_AND_ACCRUAL;
  const showCashSection =
    values.basis === ACCOUNTING_METHODS.CASH ||
    values.basis === ACCOUNTING_METHODS.CASH_AND_ACCRUAL;
  const isCashAndAccrualBasis =
    values.basis === ACCOUNTING_METHODS.CASH_AND_ACCRUAL;

  return (
    <>
      {isCashAndAccrualBasis && (
        <>
          <Divider />
          <div className="t-flex t-text-blue-80 t-items-center t-gap-1.5 t-text-body-sm-italic t-italic">
            <Info size="14" />
            <span>
              Since the basis is Cash & Accrual, both cash and accrual line
              items are to be added separately to continue.
            </span>
          </div>
        </>
      )}
      <div className="t-flex t-gap-5 t-flex-col">
        {showAccrualSection && (
          <Card.Root borderRounded="lg" variant="secondary">
            <Card.Header className="t-text-subtext t-text-text-60">
              <div className="t-flex t-items-center t-gap-2.5">
                <span>Line Items for Accrual Basis</span>
                <span className="t-text-body-sm-italic t-italic">
                  ({pluralize(accrualEntries.length, "entry", "entries")})
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              <AddJournalEntryTable
                allEntries={values.transactions}
                basisEntries={accrualEntries}
                entryType="ACCRUAL"
                maxSerialNumber={maxSerialNumber}
              />
            </Card.Body>
          </Card.Root>
        )}

        {showCashSection && (
          <Card.Root borderRounded="lg" variant="secondary">
            <Card.Header className="t-text-subtext t-text-text-60">
              <div className="t-flex t-items-center t-gap-2.5">
                <span>Line Items for Cash Basis</span>
                <span className="t-text-body-sm-italic t-italic">
                  (
                  {pluralize(
                    values.is_cash_same_as_accrual
                      ? accrualEntries.length
                      : cashEntries.length,
                    "entry",
                    "entries"
                  )}
                  )
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="t-flex t-flex-col t-gap-5">
                {isCashAndAccrualBasis && (
                  <>
                    {/* We Remove the cash entries when they are same and when they are same, we copy the accrual into cash */}
                    <Checkbox
                      checked={values.is_cash_same_as_accrual}
                      onChange={(e) => {
                        onChange(e.target.checked);
                      }}
                      label={
                        <span className="t-text-subtext t-text-text-60">
                          Same as Accrual
                        </span>
                      }
                    />
                  </>
                )}
                {!values.is_cash_same_as_accrual && (
                  <AddJournalEntryTable
                    allEntries={values.transactions}
                    basisEntries={cashEntries}
                    entryType="CASH"
                    maxSerialNumber={maxSerialNumber}
                  />
                )}
              </div>
            </Card.Body>
          </Card.Root>
        )}
      </div>

      {!cashAndAccrualMatch &&
        !values.is_cash_same_as_accrual &&
        isCashAndAccrualBasis && (
          <Banner
            type="error"
            description="Ensure total debits & credits in cash match those in accrual to proceed."
          />
        )}
    </>
  );
};
